import React, { useState, useEffect, useMemo } from "react";
import {
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Box,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SearchBar from "../../components/SearchBar";
import AddIcon from "@mui/icons-material/Add";
import SwitchToggle from "../../components/SwitchToggle";
import { useGetBadges, useDeleteBadge, useUpdateBadge } from "../../api/hooks/useBadge";
import { showSuccess, showError } from "../../helpers/toast";
import { CRITERIA_TYPE_LABELS } from "../../constants/badge";
import type { Badge } from "../../api/types/badge.types";
import Pagination from "../../components/Panigation";
import ConfirmationDialog from "../../components/ConfirmationDialog";

const ManageBadges = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [badgeToDelete, setBadgeToDelete] = useState<string | null>(null);

  // Debounce search term
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(1); // Reset to page 1 when search changes
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  // Fetch badges with pagination and search
  const { data: badgesData, isLoading: isLoadingBadges, refetch: refetchBadges, error: badgesError } = useGetBadges(
    {
      ...(debouncedSearchTerm.trim() && { search: debouncedSearchTerm.trim() }),
      page,
      limit,
      offset: (page - 1) * limit,
    },
    { 
      enabled: true,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
    }
  );

  // Extract badges and totalCount from response
  const { badges, totalCount } = useMemo(() => {
    const data = badgesData?.data;
    let badgesList: Badge[] = [];
    let total = 0;
    
    if (Array.isArray(data)) {
      badgesList = data;
      total = data.length;
    } else if (data && typeof data === 'object') {
      const dataObj = data as any;
      if ('list' in dataObj && Array.isArray(dataObj.list)) {
        badgesList = dataObj.list;
        total = dataObj.totalCount || dataObj.list.length;
      } else if ('data' in dataObj && Array.isArray(dataObj.data)) {
        badgesList = dataObj.data;
        total = dataObj.totalCount || dataObj.data.length;
      } else if (dataObj.totalCount !== undefined) {
        total = dataObj.totalCount;
      }
    }
    
    if (!Array.isArray(badgesList)) {
      badgesList = [];
    }
    
    return {
      badges: badgesList,
      totalCount: total || badgesList.length,
    };
  }, [badgesData]);

  // Calculate total pages
  const totalPages = Math.ceil(totalCount / limit);

  // Helper function to get criteria type label
  const getCriteriaTypeLabel = (badge: Badge): string => {
    const type = badge.type ?? badge.criteriaType;
    if (!type) return "-";
    
    if (typeof type === "number") {
      return CRITERIA_TYPE_LABELS[type] || "Unknown";
    }
    return String(type);
  };

  // Handle status toggle
  const { mutate: updateBadgeStatus, isPending: isUpdatingStatus } = useUpdateBadge({
    onSuccess: (response) => {
      showSuccess(response.message || "Status updated successfully");
      refetchBadges();
    },
    onError: (error: any) => {
      showError(error?.response?.data?.message || "Failed to update status");
    },
  });

  const handleToggleStatus = (badge: Badge) => {
    updateBadgeStatus({
      id: badge._id,
      data: { status: !badge.status },
    });
  };

  // Handle delete
  const { mutate: deleteBadge, isPending: isDeleting } = useDeleteBadge({
    onSuccess: (response) => {
      showSuccess(response.message || "Badge deleted successfully");
      refetchBadges();
      setDeleteDialogOpen(false);
      setBadgeToDelete(null);
    },
    onError: (error: any) => {
      showError(error?.response?.data?.message || "Failed to delete badge");
    },
  });

  const handleDelete = (id: string) => {
    setBadgeToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (badgeToDelete) {
      deleteBadge(badgeToDelete);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setBadgeToDelete(null);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  return (
    <>
      <div className="main_title">
        <h1>Manage Achievements & Badges</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -
          Manage Achievements & Badges
        </p>
      </div>

      <div className="cards">
        <div className="table_header">
          <div className="left_s">
            <SearchBar
                searchTerm={searchTerm}
                setDebouncedSearchTerm={setSearchTerm}
                placeholder="Search Badges"
                value={searchTerm}
                onChange={(e:any) => setSearchTerm(e.target.value)}
                onCross={() => {
                  setSearchTerm("");
                  setDebouncedSearchTerm("");
                }}
            />
          </div>

          <div className="right_s">
            <Button className="btn btn_primary" onClick={() => navigate("add")}>
              <AddIcon /> Add Badge
            </Button>
          </div>
        </div>

        {isLoadingBadges ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : badgesError ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <p style={{ color: "red" }}>
              {badgesError?.response?.data?.message || "Failed to load badges"}
            </p>
          </Box>
        ) : badges.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <p>No badges found</p>
          </Box>
        ) : (
          <>
            <TableContainer className="table_container">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {badges.map((badge) => (
                    <TableRow hover key={badge._id}>
                      <TableCell>
                        <b>{badge.title}</b>
                      </TableCell>
                      <TableCell>{getCriteriaTypeLabel(badge)}</TableCell>
                      <TableCell>
                        <SwitchToggle
                          defaultChecked={badge.status ?? true}
                          onChange={() => handleToggleStatus(badge)}
                          disabled={isUpdatingStatus}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="table_actions v2">
                          <IconButton
                            onClick={() => navigate("view", { state: { badge } })}
                          >
                            <img src="/static/images/eye_icon.svg" alt="View" />
                          </IconButton>
                          <IconButton
                            onClick={() => navigate("edit", { state: { badge } })}
                          >
                            <img src="/static/images/edit2_icon.svg" alt="Edit" />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDelete(badge._id)}
                            disabled={isDeleting}
                          >
                            <img
                              src="/static/images/trash2_icon.svg"
                              alt="Delete"
                            />
                          </IconButton>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {totalPages > 0 && (
              <Pagination
                module={badges}
                page={page}
                onPageChange={handlePageChange}
                totalPages={totalPages}
                totalCount={totalCount}
                setPage={setPage}
                limit={limit}
              />
            )}
          </>
        )}
      </div>

      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Badge"
        message="Are you sure you want to delete this badge? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />
    </>
  );
};

export default ManageBadges;
